import { useState } from "react"
import { Bot, User, Wrench, ShieldCheck, Lock, Check, X, ChevronDown, ChevronRight } from "lucide-react"
import { cn } from "@/lib/cn"
import { MessageMarkdown } from "@/components/domain/message-markdown"
import { MessageAttachment } from "@/components/domain/message-attachment"
import { ToolCallChip } from "@/components/domain/tool-call-chip"
import type { TicketMessage } from "@/types/ticket"

type Props = {
  msg: TicketMessage
  /** Render the tool_call / tool_result chips below the bubble. The
   *  ticket viewer flips this off for non-admin operators. */
  showToolCalls?: boolean
}

// One row in the ticket conversation. Three variants dispatched by role:
//   - role="internal_note"  → gold-bordered note card with right-side
//                             "Internal" pill and lock-icon avatar
//   - role="tool" (or assistant message with only toolCalls)
//                           → compact single-line tool event row
//   - everything else       → message-row card (red wash for human_agent,
//                             neutral for customer / AI / system)
//
// Each variant uses one of the .message-row / .human-row / .tool-row /
// .internal-note-row classes from app.css — those carry the gradient +
// border + (for the gold note) glow. Don't reach for ChatMessageBubble
// here; that primitive serves the Designer + Test panes and uses a
// different bubble look.
export function TicketMessageCard({ msg, showToolCalls = true }: Props) {
  if (msg.role === "internal_note") {
    return <InternalNoteCard msg={msg} />
  }

  // A "tool" role message OR an assistant message with toolCalls and no
  // body collapses to the compact tool-event row. (Assistant messages
  // that have BOTH content and tool calls render as a normal row and the
  // calls appear under the body via the existing ToolCallChip path.)
  const toolCalls   = Array.isArray(msg.toolCalls)   ? msg.toolCalls   as Array<Record<string, unknown>> : []
  const toolResults = Array.isArray(msg.toolResults) ? msg.toolResults as Array<Record<string, unknown>> : []
  const isToolOnly  = msg.role === "tool" || (msg.role === "assistant" && !msg.content && toolCalls.length > 0)
  if (isToolOnly) {
    return <ToolEventRow msg={msg} toolCalls={toolCalls} toolResults={toolResults} showExpand={showToolCalls} />
  }

  return <RegularMessageCard msg={msg} showToolCalls={showToolCalls} toolCalls={toolCalls} toolResults={toolResults} />
}

// ---- variants ---------------------------------------------------------

function RegularMessageCard({
  msg, showToolCalls, toolCalls, toolResults,
}: {
  msg: TicketMessage
  showToolCalls: boolean
  toolCalls:   Array<Record<string, unknown>>
  toolResults: Array<Record<string, unknown>>
}) {
  const isUser      = msg.role === "user"
  const isAssistant = msg.role === "assistant"
  const isHuman     = msg.role === "human_agent"
  const isSystem    = msg.role === "system"

  const label =
    msg.authorName?.trim()
      ? msg.authorName
      : isUser ? "Customer"
      : isAssistant ? "Agent"
      : isHuman ? "Human agent"
      : isSystem ? "System"
      : msg.role

  // Operator names get the uppercase tracking-wide treatment that mirrors
  // the "ZADAM ZERKINS" header in the design spec; customer + AI stay
  // mixed-case because that's how the audience reads naturally.
  const labelClass = isHuman
    ? "font-semibold uppercase tracking-wide text-white"
    : "font-semibold text-white"

  const time = new Date(msg.createdAt).toLocaleTimeString([], {
    hour: "numeric", minute: "2-digit",
  })

  return (
    <article className={cn("rounded-xl px-4 py-2.5", isHuman ? "human-row" : "message-row")}>
      <div className="flex gap-3">
        <RoleAvatar msg={msg} />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-baseline gap-2">
            <span className={labelClass}>{label}</span>
            <span className="text-xs text-app-subtle">{time}</span>
          </div>
          {msg.content && (
            <div className="mt-0.5 text-sm leading-tight text-neutral-100">
              {isSystem
                ? <MessageMarkdown content={msg.content} neutralizeLinks />
                : <MessageMarkdown content={msg.content} />
              }
            </div>
          )}
          {msg.files.length > 0 && (
            <div className="mt-2 flex flex-wrap items-start gap-2">
              {msg.files.map((f, i) => (
                <MessageAttachment key={i} file={f} />
              ))}
            </div>
          )}
          {showToolCalls && (toolCalls.length > 0 || toolResults.length > 0) && (
            <div className="mt-1.5 space-y-1">
              {pairCallsWithResults(toolCalls, toolResults).map((pair, i) => (
                <ToolCallChip
                  key={`tool-${i}`}
                  toolCall={pair.call}
                  toolResult={pair.result}
                  timestamp={msg.createdAt}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </article>
  )
}

function ToolEventRow({
  msg, toolCalls, toolResults, showExpand,
}: {
  msg: TicketMessage
  toolCalls:   Array<Record<string, unknown>>
  toolResults: Array<Record<string, unknown>>
  showExpand:  boolean
}) {
  const [open, setOpen] = useState(false)
  const time = new Date(msg.createdAt).toLocaleTimeString([], {
    hour: "numeric", minute: "2-digit",
  })

  // Pull the first call/result pair to drive the headline. A single
  // assistant turn typically fires one tool; if it fires multiple, the
  // expanded view (admin only) shows them all.
  const pairs = pairCallsWithResults(toolCalls, toolResults)
  const head  = pairs[0]
  const name  = head?.call?.name ?? head?.result?.name ?? msg.content ?? "tool"
  const isErr = head?.result?.is_error === true
  const status: "running" | "succeeded" | "errored" =
    isErr ? "errored" : head?.result ? "succeeded" : "running"

  const StatusIcon = isErr ? X : status === "succeeded" ? Check : Wrench
  const statusTone = isErr
    ? "text-status-error"
    : status === "succeeded"
      ? "text-green-400"
      : "text-app-muted"

  return (
    <article className="tool-row rounded-xl px-4 py-2">
      <button
        type="button"
        disabled={!showExpand}
        onClick={() => showExpand && setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center gap-3 text-left",
          showExpand && "cursor-pointer",
        )}
      >
        <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-white/10 bg-white/5 text-app-muted">
          <Wrench size={13} strokeWidth={2} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-white">Tool</span>
          <span className="text-xs text-app-subtle">{time}</span>
          <span className="h-3 w-px bg-white/10" />
          <span className={cn("inline-flex items-center gap-1 text-sm font-medium", statusTone)}>
            <StatusIcon size={13} strokeWidth={2.25} /> {name}
          </span>
          <span className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-app-muted">
            {status}
          </span>
        </div>
        {showExpand && (
          <span className="ml-auto shrink-0 text-app-subtle">
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </span>
        )}
      </button>

      {open && pairs.length > 0 && (
        <div className="mt-2 space-y-1 pl-10">
          {pairs.map((pair, i) => (
            <ToolCallChip
              key={`tool-${i}`}
              toolCall={pair.call}
              toolResult={pair.result}
              timestamp={msg.createdAt}
            />
          ))}
        </div>
      )}
    </article>
  )
}

function InternalNoteCard({ msg }: { msg: TicketMessage }) {
  const time = new Date(msg.createdAt).toLocaleTimeString([], {
    hour: "numeric", minute: "2-digit",
  })
  return (
    <article className="internal-note-row rounded-xl px-4 py-2.5">
      <div className="flex gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-vip-gold/50 bg-vip-gold/10 text-vip-goldSoft">
          <Lock size={15} strokeWidth={2.5} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-vip-goldSoft">Internal note</span>
            <span className="text-xs uppercase tracking-wide text-app-muted">{msg.authorName ?? "Note"}</span>
            <span className="text-xs text-app-subtle">{time}</span>
          </div>
          <p className="mt-1 max-w-3xl whitespace-pre-wrap text-sm leading-5 text-[#ffe3a3]">
            {msg.content}
          </p>
        </div>
      </div>
    </article>
  )
}

// ---- shared helpers ---------------------------------------------------

// Avatar with a three-tier fallback: real photo URL → letter tile in a
// role-based tone (warm yellow for customers, brand-red gradient for
// operators, gold for AI) → role-icon fallback when there's no name to
// derive a letter from. Sizing matches the design spec at 11×11.
function RoleAvatar({ msg }: { msg: TicketMessage }) {
  const isUser      = msg.role === "user"
  const isAssistant = msg.role === "assistant"
  const isHuman     = msg.role === "human_agent"
  const isSystem    = msg.role === "system"

  if (msg.avatarUrl) {
    return (
      <img
        src={msg.avatarUrl}
        alt={msg.authorName ?? "?"}
        loading="lazy"
        className="h-9 w-9 shrink-0 rounded-full border border-white/10 object-cover"
      />
    )
  }

  if (msg.authorName) {
    const initial = msg.authorName.trim()[0]?.toUpperCase() ?? "?"
    const tone = isUser
      ? "bg-[#ffdc5e] text-white"
      : isHuman
        ? "bg-gradient-to-br from-red-500 to-red-700 text-white"
        : isAssistant
          ? "border border-vip-gold/40 bg-vip-goldMute text-vip-goldSoft"
          : "border border-white/10 bg-white/5 text-app-muted"
    return (
      <div className={cn(
        "grid h-9 w-9 shrink-0 place-items-center rounded-full text-base font-bold",
        tone,
      )}>
        {initial}
      </div>
    )
  }

  const Icon = isUser ? User : isHuman ? User : isSystem ? ShieldCheck : Bot
  return (
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 bg-white/5 text-app-muted">
      <Icon size={16} strokeWidth={1.8} />
    </div>
  )
}

// Pair each tool_call with its matching tool_result (Anthropic tool_use_id
// when present, falling back to name match). Calls without a result
// render as in-flight; orphan results tail the list so nothing is lost.
type ToolCall   = { id?: string; name?: string; input?: unknown }
type ToolResult = { id?: string; name?: string; output?: unknown; is_error?: boolean }

function pairCallsWithResults(
  calls: Array<Record<string, unknown>>,
  results: Array<Record<string, unknown>>,
): Array<{ call?: ToolCall; result?: ToolResult }> {
  const remainingResults = [...results] as ToolResult[]
  const pairs: Array<{ call?: ToolCall; result?: ToolResult }> = []

  for (const c of calls as ToolCall[]) {
    let matchIdx = -1
    if (c.id) matchIdx = remainingResults.findIndex((r) => r.id === c.id)
    if (matchIdx === -1) matchIdx = remainingResults.findIndex((r) => r.name === c.name)
    const match = matchIdx >= 0 ? remainingResults.splice(matchIdx, 1)[0] : undefined
    pairs.push({ call: c, result: match })
  }
  for (const orphan of remainingResults) pairs.push({ result: orphan })
  return pairs
}
